import {
  createContext,
  useContext,
  useMemo,
  type FC,
  type ReactNode,
} from "react";

import useHousehold from "src/contexts/household-context";
import type { Asset } from "src/types/snugtotal";
import {
  buildDocumentRows,
  isDocRowComplete,
  type DocRow,
} from "src/utils/document-rows";
import { useAssetsStore } from "src/hooks/snug/use-assets";
import { usePeopleRolesStore } from "src/hooks/snug/use-peopleRoles";
import { useHistoryStore } from "src/hooks/snug/use-histories";
import { useIdentificationStore } from "src/hooks/snug/use-identification";
import { useOtherLegalStore } from "src/hooks/snug/use-otherLegal";
import { useMedicalAllergyStore } from "src/hooks/snug/use-medicalAllergies";
import { useMedicalHistoryStore } from "src/hooks/snug/use-medicalHistory";
import { useMedicalMedicationStore } from "src/hooks/snug/use-medicalMedications";
import { useDebtStore } from "src/hooks/snug/use-debts";
import { useUtilitiesStore } from "src/hooks/snug/use-utilities";
import { useStorageStore } from "src/hooks/snug/use-storage";
import { useDigitalAssetStore } from "src/hooks/snug/use-digitalassets";
import { usePasscodeStore } from "src/hooks/snug/use-passcodes";
import { useLegacyStore } from "src/hooks/snug/use-legacy";
import { useOtherUserDataStore } from "src/hooks/snug/use-otherUserData";

export type DocStatus = "complete" | "in_progress" | "not_started";

export interface ModuleProgress {
  key: string;
  count: number;
}

export interface ProgressValue {
  /** ud_id the counts are computed for (the currently-viewed member). */
  udId: string | null;
  loading: boolean;
  documents: {
    complete: number;
    total: number;
    status: DocStatus;
    items: DocRow[];
  };
  assets: {
    count: number;
    items: Asset[];
  };
  letter: {
    hasContent: boolean;
  };
  modules: {
    personal: number;
    legal: number;
    medical: number;
    financial: number;
    property: number;
    digital: number;
    legacy: number;
  };
}

const EMPTY: ProgressValue = {
  udId: null,
  loading: true,
  documents: { complete: 0, total: 0, status: "not_started", items: [] },
  assets: { count: 0, items: [] },
  letter: { hasContent: false },
  modules: {
    personal: 0,
    legal: 0,
    medical: 0,
    financial: 0,
    property: 0,
    digital: 0,
    legacy: 0,
  },
};

const ProgressContext = createContext<ProgressValue>(EMPTY);

interface ProgressProviderProps {
  children: ReactNode;
}

/**
 * Aggregates light "how much is filled in" progress for the currently-viewed
 * household member, so the homepage and the side-nav can show counts without
 * each fetching independently. Mounted once at the dashboard layout level.
 *
 * Counts are intentionally lightweight (entry counts + document completion),
 * not a formal scoring system. They reflect the member identified by the
 * household context's `selectedUdId` (defaults to the head of household).
 */
export const ProgressProvider: FC<ProgressProviderProps> = ({ children }) => {
  const { selectedHousehold, selectedUdId, loading: householdLoading } =
    useHousehold();
  const head = selectedHousehold?.head_of_household;
  const udId = selectedUdId || head?.ud_id || null;

  const { assets, assetsLoading } = useAssetsStore(udId);
  const { peopleRoles } = usePeopleRolesStore(udId);
  const { history } = useHistoryStore(udId);
  const { identifications } = useIdentificationStore(udId);
  const { otherLegal } = useOtherLegalStore(udId);
  const { medicalAllergy } = useMedicalAllergyStore(udId);
  const { medicalHistory } = useMedicalHistoryStore(udId);
  const { medicalMedication } = useMedicalMedicationStore(udId);
  const { debt } = useDebtStore(udId);
  const { utilities } = useUtilitiesStore(udId);
  const { storage } = useStorageStore(udId);
  const { digitalAsset } = useDigitalAssetStore(udId);
  const { passcodes } = usePasscodeStore(udId);
  const { legacy } = useLegacyStore(udId);
  const { otherUserData } = useOtherUserDataStore(udId);

  const value = useMemo<ProgressValue>(() => {
    const byType = (type: string) =>
      (assets ?? []).filter((a) => a.asset_type === type);

    // Documents are a household concept — derive per-person rows from the
    // already-loaded household data and roll them up.
    const docItems = buildDocumentRows(selectedHousehold);
    const docTotal = docItems.length;
    const docComplete = docItems.filter((r) => isDocRowComplete(r.status))
      .length;
    const docStatus: DocStatus =
      docTotal > 0 && docComplete === docTotal
        ? "complete"
        : docComplete > 0
        ? "in_progress"
        : "not_started";

    return {
      udId,
      loading: householdLoading || (!!udId && assetsLoading),
      documents: {
        complete: docComplete,
        total: docTotal,
        status: docStatus,
        items: docItems,
      },
      assets: { count: (assets ?? []).length, items: assets ?? [] },
      letter: {
        hasContent: Boolean(otherUserData?.words_of_wisdom?.trim()),
      },
      modules: {
        personal:
          (peopleRoles?.length ?? 0) +
          (history?.length ?? 0) +
          (identifications?.length ?? 0),
        legal: (otherLegal?.length ?? 0) + byType("business_interest").length,
        medical:
          (medicalAllergy?.length ?? 0) +
          (medicalHistory?.length ?? 0) +
          (medicalMedication?.length ?? 0),
        financial: byType("financial_account").length + (debt?.length ?? 0),
        property:
          byType("realestate").length +
          byType("personal_property").length +
          (utilities?.length ?? 0) +
          (storage?.length ?? 0),
        digital: (digitalAsset?.length ?? 0) + (passcodes?.length ?? 0),
        legacy: legacy ? 1 : 0,
      },
    };
  }, [
    udId,
    householdLoading,
    assetsLoading,
    assets,
    selectedHousehold,
    otherUserData,
    peopleRoles,
    history,
    identifications,
    otherLegal,
    medicalAllergy,
    medicalHistory,
    medicalMedication,
    debt,
    utilities,
    storage,
    digitalAsset,
    passcodes,
    legacy,
  ]);

  return (
    <ProgressContext.Provider value={value}>
      {children}
    </ProgressContext.Provider>
  );
};

export const useProgress = (): ProgressValue => useContext(ProgressContext);

export default useProgress;
